import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plug, Copy, Send, Radio, CheckCircle2, Webhook, Cpu, ShieldAlert, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import ROICalculator from "@/components/ROICalculator";

const LOGOS = {
  zenoti: "Z", boulevard: "B", square: "S",
};

export default function Integrations() {
  const { branding, money } = useBranding();
  const qc = useQueryClient();
  const [storeNum, setStoreNum] = useState("482");
  const navigate = useNavigate();

  const { data: stores } = useQuery({ queryKey: ["stores"], queryFn: () => api.get("/stores").then((r) => r.data) });
  const { data, refetch } = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/integrations").then((r) => r.data) });
  const { data: sim, refetch: refetchSim } = useQuery({ queryKey: ["sim-status"], queryFn: () => api.get("/sim/stream/status").then((r) => r.data), refetchInterval: 4000 });
  const running = !!sim?.running;
  const toggleStream = async () => {
    try {
      if (running) { await api.post("/sim/stream/stop"); toast.info("Live sensor stream stopped"); }
      else { await api.post("/sim/stream/start", { store_number: storeNum }); toast.success(`Live sensor stream started on #${storeNum}`); }
      refetchSim();
    } catch { toast.error("Corporate access required"); }
  };

  useEffect(() => { if (stores?.length) setStoreNum((s) => stores.find((x) => x.number === s) ? s : stores[0].number); }, [stores]);

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied to clipboard"); };

  const toggle = async (key) => {
    try { await api.put(`/integrations/${key}`); refetch(); toast.success("Connector updated"); }
    catch { toast.error("Corporate access required"); }
  };

  const sendTestSale = async (key) => {
    try {
      const amount = [19, 23, 30][Math.floor(Math.random() * 3)];
      const { data: res } = await api.post(`/pos/webhook/${key}`, {
        store_number: storeNum, amount, service_name: "Adult haircut",
      });
      if (res.matched) toast.success(`Sale ${money(amount)} reconciled — a flag was cleared & footage discarded`);
      else toast.info(`Sale ${money(amount)} received — no open flag to match right now`);
      qc.invalidateQueries();
      refetch();
    } catch { toast.error("Could not send test sale"); }
  };

  const simulateSeat = async () => {
    try {
      const minutes = [8, 14, 18, 22, 26][Math.floor(Math.random() * 5)];
      const { data: res } = await api.post("/ingest/occupancy", { store_number: storeNum, minutes_seated: minutes, occupied: true });
      toast.success(`Seat packet ingested — chair now "${res.status}" (${minutes} min). Open Live Floor View to see it.`);
      qc.invalidateQueries({ queryKey: ["store"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
    } catch { toast.error("Could not ingest packet"); }
  };

  const simulateTamper = async () => {
    try {
      const { data: res } = await api.post("/simulate/tamper");
      toast.warning(res.alert.message);
      qc.invalidateQueries();
    } catch { toast.error("Could not simulate tamper — corporate access required"); }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Integrations · Data Feeds</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
        Connect your POS and your seats
      </h1>
      <p className="mt-2 text-slate-400 max-w-2xl">
        Sales flow in from your point-of-sale so every service can be matched automatically. Chairs stream
        occupancy from the seat sensors so the floor updates in real time. No changes to your registers.
      </p>

      {/* Test store selector */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Test against store</span>
        <Select value={storeNum} onValueChange={setStoreNum}>
          <SelectTrigger className="w-56 bg-[#1E2638] border-[#2A3550]" data-testid="integrations-store-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1E2638] border-[#2A3550]">
            {(stores || []).map((s) => (
              <SelectItem key={s.id} value={s.number}>#{s.number} · {s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-5">
        <div className="surface-card p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Sales received</div>
          <div className="mt-2 font-display text-3xl font-light" data-testid="stat-txn-total">{data?.stats.total ?? 0}</div>
        </div>
        <div className="surface-card p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Auto-reconciled</div>
          <div className="mt-2 font-display text-3xl font-light" style={{ color: branding.accent_color }} data-testid="stat-txn-matched">
            {data?.stats.reconciled ?? 0}
          </div>
        </div>
        <div className="surface-card p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Live sensors</div>
          <div className="mt-2 font-display text-xl sm:text-2xl font-light leading-tight text-emerald-400">Streaming</div>
        </div>
      </div>

      {/* POS Connectors */}
      {/* ROI calculator + live sensor stream */}
      <div className="mt-6"><ROICalculator /></div>

      <div className="mt-6 surface-card p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="live-stream-card">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${running ? "bg-emerald-400 pulse-status" : "bg-slate-500"}`} />
            <h2 className="font-display text-lg font-medium">Live sensor stream{running ? " — running" : ""}</h2>
          </div>
          <p className="mt-1 text-sm text-slate-400">Simulates the ESP32 seat sensors + POS on <strong className="text-slate-200">#{storeNum}</strong>: chairs fill and empty on real weight lifecycles, sessions stitch, and sales reconcile live — so the floor moves on its own while a prospect watches.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          <button onClick={toggleStream} data-testid="stream-toggle-btn"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200"
            style={{ backgroundColor: running ? "#ef4444" : branding.primary_color }}>
            <Radio className="h-4 w-4" /> {running ? "Stop stream" : "Start live sensor stream"}
          </button>
          <button onClick={() => navigate(`/floor?store=${storeNum}`)} data-testid="open-floor-btn"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border border-[#2A3550] bg-[#1E2638] text-slate-100 hover:border-[#38BDF8] transition-colors duration-200">
            Open Live Floor View <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <h2 className="mt-10 font-display text-xl font-medium flex items-center gap-2">
        <Webhook className="h-5 w-5 text-slate-400" /> POS Connectors
      </h2>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-5 min-w-0">
        {(data?.providers || []).map((p, i) => (
          <motion.div
            key={p.key}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="surface-card p-6 min-w-0"
            data-testid={`connector-${p.key}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-[#232C40] flex items-center justify-center font-display font-semibold">
                  {LOGOS[p.key]}
                </div>
                <div>
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs text-slate-400">{p.enabled ? "Connected" : "Disabled"}</div>
                </div>
              </div>
              <Switch checked={p.enabled} onCheckedChange={() => toggle(p.key)} data-testid={`toggle-${p.key}`} />
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-1.5">Webhook URL</div>
              <button
                onClick={() => copy(p.webhook_url)}
                className="w-full flex items-center gap-2 rounded-lg bg-[#171E2E] border border-[#2A3550] px-3 py-2 text-left hover:border-[#334063] transition-colors duration-200"
                data-testid={`copy-webhook-${p.key}`}
              >
                <span className="font-mono text-xs text-slate-400 truncate flex-1 min-w-0">{p.webhook_url}</span>
                <Copy className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              </button>
            </div>

            <button
              onClick={() => sendTestSale(p.key)}
              disabled={!p.enabled}
              data-testid={`test-sale-${p.key}`}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: branding.primary_color }}
            >
              <Send className="h-4 w-4" /> Send a test sale
            </button>
          </motion.div>
        ))}
      </div>

      {/* Live occupancy feed */}
      <h2 className="mt-10 font-display text-xl font-medium flex items-center gap-2">
        <Radio className="h-5 w-5 text-slate-400" /> Live Occupancy Feed
      </h2>
      <div className="mt-4 surface-card p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">
        <div className="lg:col-span-2 min-w-0">
          <p className="text-sm text-slate-400">
            Each seat's ESP32 transmitter posts a timestamped occupancy packet to this endpoint the moment
            weight crosses the 40&nbsp;lb gate. The floor map
            updates instantly and the staged timer begins.
          </p>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-1.5">Ingestion endpoint</div>
            <button
              onClick={() => copy(data?.occupancy_endpoint || "")}
              className="w-full flex items-center gap-2 rounded-lg bg-[#171E2E] border border-[#2A3550] px-3 py-2 text-left hover:border-[#334063] transition-colors duration-200"
              data-testid="copy-occupancy-endpoint"
            >
              <Cpu className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="font-mono text-xs text-slate-400 truncate flex-1 min-w-0">{data?.occupancy_endpoint}</span>
              <Copy className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            </button>
          </div>
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <button
            onClick={simulateSeat}
            data-testid="simulate-seat-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium border border-[#334063] text-white hover:bg-[#232C40] transition-colors duration-200"
          >
            <Radio className="h-4 w-4" /> Simulate a seat sitting down
          </button>
          <p className="mt-2 text-xs text-slate-400 text-center">Then open Live Floor View to watch it change.</p>
          <button
            onClick={simulateTamper}
            data-testid="simulate-tamper-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors duration-200"
          >
            <ShieldAlert className="h-4 w-4" /> Simulate a tamper event
          </button>
        </div>
      </div>

      {/* Recent transactions */}
      <h2 className="mt-10 font-display text-xl font-medium">Recent inbound sales</h2>
      <div className="mt-4 surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pos-transactions-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-[#2A3550]">
                <th className="px-5 py-4 font-semibold">Source</th>
                <th className="px-5 py-4 font-semibold">Store</th>
                <th className="px-5 py-4 font-semibold">Transaction</th>
                <th className="px-5 py-4 font-semibold">Amount</th>
                <th className="px-5 py-4 font-semibold">Result</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_transactions || []).map((t) => (
                <tr key={t.id} className="border-b border-[#2A3550]">
                  <td className="px-5 py-3.5 capitalize">{t.provider}</td>
                  <td className="px-5 py-3.5 text-slate-400">#{t.store_number}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-slate-400">{t.transaction_id}</td>
                  <td className="px-5 py-3.5">{money(t.amount)}</td>
                  <td className="px-5 py-3.5">
                    {t.matched ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-400">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Reconciled
                      </span>
                    ) : (
                      <span className="text-slate-400">No match</span>
                    )}
                  </td>
                </tr>
              ))}
              {data?.recent_transactions?.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                  No sales received yet. Send a test sale above to see reconciliation in action.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
